import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
// The CSP build loads its web worker from a separate file instead of
// assembling it from stringified code at runtime. The default build breaks in
// the production bundle: Babel transpiles it and leaves helpers outside the
// stringified worker code, so the worker fails and the map never loads.
// The worker file is copied to public/ by the prestart/prebuild scripts.
import maplibregl from 'maplibre-gl/dist/maplibre-gl-csp';
import 'maplibre-gl/dist/maplibre-gl.css';
import { useWorkspace } from './WorkspaceContext.js';
import {
  ACTION_META, ACTION_TYPES, BUILDING_STYLE, formatWaterLevel, getDangerClassStyle,
} from './workspaceConstants.js';
import * as constants from '../constants.js';

// Read-only 3D view of the workspace. It shows the same data as
// the Leaflet map -- result rasters, buildings and Maßnahmen -- draped over a
// terrain model, with the buildings extruded. Everything that edits (drawing,
// erasing, the Maßnahmen dialog) stays on the 2D map.

// Terrain: Mapzen/Tilezen "Terrarium" elevation tiles from the AWS Open Data
// registry -- free, no key. ~30 m SRTM in Germany, so hills and valleys come
// out, single dams and ditches do not.
const DEM_TILES = 'https://s3.amazonaws.com/elevation-tiles-prod/terrarium/{z}/{x}/{y}.png';
const DEM_MAX_ZOOM = 15;

// The buildings carry no height of their own, so every one is extruded to the
// same height -- roughly a two-storey house.
const BUILDING_HEIGHT_M = 8;

// Terrain heights are drawn 2.5 times their real value, so that the gentle
// relief typical of the regions is visible at all.
const TERRAIN_EXAGGERATION = 2.5;

// Result rasters, bottom to top, matching the zIndex order on the 2D map.
const RESULT_RASTERS = [
  { key: 'flowPathsStatic', endpoint: 'get_accumulation_mask_tile', opacity: 1 },
  { key: 'discharge', endpoint: 'get_discharge_tile', opacity: 1 },
  { key: 'waterLevel', endpoint: 'get_water_level_tile', opacity: 0.6 },
];

maplibregl.setWorkerUrl(`${process.env.PUBLIC_URL}/maplibre-gl-csp-worker.js`);

const backendOrigin = `${window.location.protocol}//${constants.BASE_URL}`;

const resultTileUrl = (endpoint, scenarioId, version = 0) =>
  `${backendOrigin}/${endpoint}/${scenarioId}/{z}/{x}/{y}.png?v=${version}`;

const toLngLat = (points) => points.map(([lat, lng]) => [lng, lat]);

const closeRing = (ring) => {
  if (ring.length === 0) return ring;
  const [first, last] = [ring[0], ring[ring.length - 1]];
  return first[0] === last[0] && first[1] === last[1] ? ring : [...ring, first];
};

const emptyCollection = { type: 'FeatureCollection', features: [] };

const buildingFeatures = (buildings, pendingDeletions, dangerClasses, useDangerColours) => ({
  type: 'FeatureCollection',
  features: buildings
    .filter((building) => !pendingDeletions.includes(building.building_id))
    .map((building) => ({
      type: 'Feature',
      properties: {
        color: useDangerColours
          ? getDangerClassStyle(dangerClasses[building.building_id]).fillColor
          : BUILDING_STYLE.fillColor,
      },
      geometry: { type: 'Polygon', coordinates: [closeRing(toLngLat(building.building_outline))] },
    })),
});

const actionFeatures = (actions, visibleTypes, pendingDeletions) => ({
  type: 'FeatureCollection',
  features: visibleTypes.flatMap((type) => {
    const meta = ACTION_META[type];
    return actions[type]
      .filter((item) => !pendingDeletions.some(
        (entry) => entry.type === type && entry.id === item[meta.idKey]
      ))
      .map((item) => {
        const coords = toLngLat(item[meta.geometryKey]);
        return {
          type: 'Feature',
          properties: { color: meta.color },
          geometry: meta.geometry === 'polygon'
            ? { type: 'Polygon', coordinates: [closeRing(coords)] }
            : { type: 'LineString', coordinates: coords },
        };
      });
  }),
});

const baseStyle = {
  version: 8,
  sources: {
    osm: {
      type: 'raster',
      tiles: ['https://tile.openstreetmap.org/{z}/{x}/{y}.png'],
      tileSize: 256,
      maxzoom: 19,
      attribution: '&copy; <a href="https://www.openstreetmap.org/copyright">OpenStreetMap</a> contributors',
    },
    dem: {
      type: 'raster-dem',
      tiles: [DEM_TILES],
      encoding: 'terrarium',
      tileSize: 256,
      maxzoom: DEM_MAX_ZOOM,
      attribution: 'Terrain: <a href="https://registry.opendata.aws/terrain-tiles/">Mapzen Terrain Tiles</a>',
    },
    // Hillshade gets its own copy of the DEM -- MapLibre advises against
    // sharing one raster-dem source between terrain and hillshade.
    hillshadeDem: {
      type: 'raster-dem',
      tiles: [DEM_TILES],
      encoding: 'terrarium',
      tileSize: 256,
      maxzoom: DEM_MAX_ZOOM,
    },
    // The result rasters are part of the style from the start, hidden and
    // pointing at no scenario yet: MapLibre only drapes layers over the
    // terrain that already exist when the terrain is switched on, so a raster
    // added later would never show up. A scenario change swaps their tile
    // URLs instead (see below). Hidden layers load no tiles.
    ...Object.fromEntries(RESULT_RASTERS.map(({ key, endpoint }) => [`result-${key}`, {
      type: 'raster',
      tiles: [resultTileUrl(endpoint, 0)],
      tileSize: 256,
      attribution: 'Rapid Resilience',
    }])),
    buildings: { type: 'geojson', data: emptyCollection },
    actions: { type: 'geojson', data: emptyCollection },
  },
  layers: [
    { id: 'osm', type: 'raster', source: 'osm' },
    {
      id: 'hillshade', type: 'hillshade', source: 'hillshadeDem',
      paint: { 'hillshade-exaggeration': 0.35, 'hillshade-shadow-color': '#3a3a3a' },
    },
    ...RESULT_RASTERS.map(({ key, opacity }) => ({
      id: `result-${key}`, type: 'raster', source: `result-${key}`,
      layout: { visibility: 'none' },
      paint: { 'raster-opacity': opacity },
    })),
    {
      id: 'action-areas', type: 'fill', source: 'actions',
      filter: ['==', ['geometry-type'], 'Polygon'],
      paint: { 'fill-color': ['get', 'color'], 'fill-opacity': 0.35 },
    },
    {
      id: 'action-lines', type: 'line', source: 'actions',
      paint: { 'line-color': ['get', 'color'], 'line-width': 4 },
    },
    {
      id: 'buildings-3d', type: 'fill-extrusion', source: 'buildings',
      paint: {
        'fill-extrusion-color': ['get', 'color'],
        'fill-extrusion-height': BUILDING_HEIGHT_M,
        'fill-extrusion-base': 0,
        'fill-extrusion-opacity': 0.9,
      },
    },
  ],
};

const Workspace3DMap = () => {
  const {
    center,
    buildings,
    pendingBuildingDeletions,
    actions,
    pendingActionDeletions,
    activeScenario,
    activeScenarioId,
    layers,
    dangerClasses,
    resultsVersion,
    waterLevelProbe,
    probeWaterLevel,
    clearWaterLevelProbe,
  } = useWorkspace();

  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const [styleReady, setStyleReady] = useState(false);
  const popupRef = useRef(null);

  // Clicks query the water level, as on the 2D map. The map is created once,
  // so its click listener reads the current handler through a ref.
  const canProbe = Boolean(activeScenario) && activeScenario.status === 'done';
  const handleClickRef = useRef(null);
  handleClickRef.current = (lngLat) => {
    if (canProbe) probeWaterLevel(lngLat.lat, lngLat.lng);
  };

  useEffect(() => {
    const map = new maplibregl.Map({
      container: containerRef.current,
      style: baseStyle,
      center: [center.long, center.lat],
      zoom: 16,
      pitch: 60,
      bearing: -20,
      maxPitch: 80,
      // The result tiles are behind the same session cookie as on the 2D map,
      // where Leaflet sends it via crossOrigin="use-credentials".
      transformRequest: (url) => (url.startsWith(backendOrigin)
        ? { url, credentials: 'include' }
        : { url }),
    });
    map.addControl(new maplibregl.NavigationControl({ visualizePitch: true }), 'top-right');
    map.addControl(new maplibregl.ScaleControl({ unit: 'metric' }), 'bottom-left');
    // With terrain on, MapLibre resolves the click to the point on the
    // ground under the cursor, not on the flat map plane.
    map.on('click', (e) => handleClickRef.current(e.lngLat));
    map.on('load', () => {
      map.setTerrain({ source: 'dem', exaggeration: TERRAIN_EXAGGERATION });
      map.setSky({ 'sky-color': '#b9d8f2', 'horizon-color': '#eef4fa', 'sky-horizon-blend': 0.6 });
      setStyleReady(true);
    });
    mapRef.current = map;
    return () => {
      map.remove();
      mapRef.current = null;
    };
    // The map is created once; later centre changes are not followed.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (popupRef.current) {
      // Replaced or withdrawn by us, not closed by the user -- the probe
      // must survive this.
      popupRef.current.off('close', popupRef.current.handleClose);
      popupRef.current.remove();
      popupRef.current = null;
    }
    if (!styleReady || !canProbe || !waterLevelProbe) return;
    const { lat, lng } = waterLevelProbe;
    const popup = new maplibregl.Popup({ closeOnClick: true })
      .setLngLat([lng, lat])
      .setText(formatWaterLevel(waterLevelProbe))
      .addTo(mapRef.current);
    popup.handleClose = () => clearWaterLevelProbe(lat, lng);
    popup.on('close', popup.handleClose);
    popupRef.current = popup;
  }, [styleReady, canProbe, waterLevelProbe, clearWaterLevelProbe]);

  // Same visibility rules as the 2D map, minus the ones for drawing/erasing.
  const showBuildings = !activeScenarioId || layers.buildings;
  const useDangerColours = Boolean(activeScenario) && activeScenario.status === 'done';
  const visibleActionTypes = layers.actions
    ? ACTION_TYPES.filter((type) => layers.actionTypes[type])
    : [];

  useEffect(() => {
    if (!styleReady) return;
    mapRef.current.getSource('buildings').setData(showBuildings
      ? buildingFeatures(buildings, pendingBuildingDeletions, dangerClasses, useDangerColours)
      : emptyCollection);
  }, [styleReady, showBuildings, buildings, pendingBuildingDeletions, dangerClasses, useDangerColours]);

  const visibleActionKey = visibleActionTypes.join(',');
  useEffect(() => {
    if (!styleReady) return;
    mapRef.current.getSource('actions').setData(
      actionFeatures(actions, visibleActionKey ? visibleActionKey.split(',') : [], pendingActionDeletions)
    );
  }, [styleReady, actions, visibleActionKey, pendingActionDeletions]);

  useEffect(() => {
    if (!styleReady) return;
    const map = mapRef.current;
    RESULT_RASTERS.forEach(({ key, endpoint }) => {
      if (activeScenarioId) {
        map.getSource(`result-${key}`).setTiles([resultTileUrl(endpoint, activeScenarioId, resultsVersion)]);
      }
    });
  }, [styleReady, activeScenarioId, resultsVersion]);

  const rasterKey = RESULT_RASTERS.map(({ key }) => (layers[key] ? '1' : '0')).join('');
  useEffect(() => {
    if (!styleReady) return;
    const map = mapRef.current;
    RESULT_RASTERS.forEach(({ key }, index) => {
      const visible = Boolean(activeScenarioId) && rasterKey[index] === '1';
      map.setLayoutProperty(`result-${key}`, 'visibility', visible ? 'visible' : 'none');
    });
  }, [styleReady, activeScenarioId, rasterKey]);

  return (
    <Box sx={{ position: 'relative', height: '100%', width: '100%' }}>
      <div ref={containerRef} style={{ height: '100%', width: '100%' }} />

      {activeScenarioId && layers.flowPathsAnimated && (
        <Box sx={{
          position: 'absolute', top: '12px', right: '52px', zIndex: 900, maxWidth: 220,
          background: 'rgba(255,255,255,0.92)', border: '1px solid #aaa', borderRadius: '5px',
          px: 1.5, py: 0.75,
        }}>
          <Typography variant="caption" color="text.secondary" component="div" sx={{ lineHeight: 1.3 }}>
            Animierte Fließwege gibt es nur in der 2D-Ansicht.
          </Typography>
        </Box>
      )}
    </Box>
  );
};

export default Workspace3DMap;
